import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { GoldRateCalculator } from "@/components/GoldRateCalculator";
import { FaqSection } from "@/components/FaqSection";
import { goldRatesQueryOptions } from "@/lib/nonblocking-rates";
import { SITE_URL, DEFAULT_OG_IMAGE, hreflangLinks } from "@/lib/seo";

const PATH = "/today-gold-rate-calculator-india";
const TITLE = "Today Gold Rate Calculator India — 22Kt, 24 Carat Per 10 Gram Live";
const DESC = "Free today gold price calculator India — live rate per 10 gram, per gram, per 8 gram, per tola in 24 carat, 22Kt, 21K, 18K, 14K. Delhi, Mumbai, Chennai, Kolkata, Bangalore sarafa rate.";

const FAQS = [
  {
    q: "How does today gold rate calculator India work for 24 carat and 22Kt?",
    a: "Karat dropdown se '24K (99.9% pure)' ya '22K / 22Kt (Jewellery)' select karein, unit mein '10 Gram' (India ka standard) chunein, city select karein — calculator turant IBJA-style live rate ke hisaab se total dikhata hai. 22Kt rate = 24 carat rate × 22/24 (yaani 91.67%). Bridal jewellery aur daily-wear ornaments 22Kt mein aate hain, is liye jewellers 22Kt rate quote karte hain.",
  },
  {
    q: "Today gold price calculator India kaunsa formula use karta hai?",
    a: "Formula: (COMEX spot USD/oz ÷ 31.1035) × INR/USD × 10 × 1.10 (customs+GST buffer) + ₹11,000 local premium × city multiplier = per 10g 24 carat rate. Yeh IBJA (India Bullion and Jewellers Association) ke published rate ke ±1% mein rehta hai. 22Kt aur baaki karat 24 carat se derive hote hain.",
  },
  {
    q: "Today gold rate calculator India 24 carat — kya import duty include hai?",
    a: "Haan. Formula mein 10% multiplier lagaya gaya hai jo India ki approximately 15% customs duty + 3% GST + Agriculture Infrastructure & Development Cess (AIDC) ka combined effect capture karta hai. Isi wajah se hamara 24 carat per 10 gram rate MCX spot se ~15-18% upar aata hai, exactly jaisa Delhi/Mumbai sarafa mein dikhta hai.",
  },
  {
    q: "Gold price calculator 22K India ka use bridal jewellery ke liye kaise karein?",
    a: "Necklace, bangles, jhumka, mangalsutra almost sab 22Kt (916 hallmark) mein banti hain. Calculator mein 22K select karein, unit '10 Gram' aur quantity apne set ke total grams ke hisaab se (jaise bridal set 60g hai to 6 units × 10 gram) daalein. Result sirf gold value dikhata hai — jeweller separately 8–25% making charges + 3% GST + hallmarking fee add karega.",
  },
  {
    q: "Today gold rate calculator India 22kt — Delhi aur Chennai mein farq kyun?",
    a: "Delhi India ka base bullion hub hai. Mumbai almost identical rate deta hai. Chennai (Tamil Nadu) mein state octroi + higher retail demand ki wajah se ~0.4% premium lagta hai — south India mein weddings mein heavy gold buying culture ki wajah se demand consistently zyada rehti hai. Calculator har city ka realistic premium apply karta hai.",
  },
  {
    q: "8 gram gold rate India mein kyun calculate hota hai?",
    a: "Coin form mein (jaise MMTC-PAMP, Tanishq gold coins) 8 gram aur 5 gram popular denominations hain — Dhanteras aur Akshaya Tritiya par log 8g ya 10g coin gift karte hain. Unit dropdown se '8 Gram' chunein aur karat select karein (coins ke liye 24 carat select karein). Calculator turant coin ka pure gold value dikhata hai.",
  },
  {
    q: "Kya calculator India mein tola aur per gram bhi support karta hai?",
    a: "Haan. Purane generation aur north India ke sarafa aksar 'tola' (11.6638 gram) use karte hain, aur small quantities (jaise 1g, 2g coin ya nose pin) ke liye per gram convenient hai. Dropdown mein 'Tola', '1 Gram', ya 'Kilogram' select karein — calculator internally sab conversions handle karta hai using metric ratios.",
  },
  {
    q: "Gold price calculator India mein GST aur making charges kaise add karein?",
    a: "Calculator sirf pure gold metal value dikhata hai. Final jewellery bill = (calculator value) + making charges (usually 8-25% of gold value ya ₹500-3000/gram flat) + 3% GST on total (gold + making) + hallmarking ₹45. Investment gold bars/coins par making charges bahut minimal (~1-3%) hote hain, is liye pure calculator value se close aata hai.",
  },
  {
    q: "22Kt aur 24 carat mein invest karne ke liye kaunsa better hai?",
    a: "Investment ke liye 24 carat (99.9% pure gold coins/bars) best hai kyunki resale par full purity value milti hai. 22Kt jewellery kharidne par resale ke waqt making charges recover nahi hote — jeweller sirf gold value deta hai (minus 5-10% deduction). Wedding/gifting ke liye 22Kt fine hai, pure investment ke liye SGB (Sovereign Gold Bond) ya 24K bar consider karein.",
  },
  {
    q: "Kya yeh gold calculator daily update hota hai?",
    a: "Haan — data har 10 minute mein refresh hota hai (COMEX spot + live USD/INR interbank rate). Chart last 6 months ka daily trend dikhata hai jisme aap 24 carat aur 22Kt dono ki movement compare kar sakte hain. Weekend (Saturday-Sunday) par international markets band hote hain, is liye Friday closing rate hi Monday tak dikhega.",
  },
];

export const Route = createFileRoute("/today-gold-rate-calculator-india")({
  loader: ({ context }) => context.queryClient.prefetchQuery(goldRatesQueryOptions),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_IN" },
      { property: "og:image", content: DEFAULT_OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: DEFAULT_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }, ...hreflangLinks(PATH)],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: IndiaCalcPage,
});

function IndiaCalcPage() {
  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <h1 className="text-3xl font-black md:text-4xl">
          Today Gold Rate Calculator India — 22Kt, 24 Carat Per 10 Gram Live
        </h1>
        <p className="mt-2 text-muted-foreground">
          Live <strong>today gold price calculator India</strong> — per 10 gram, per gram, per 8 gram aur per tola sabhi karat (24 carat, 22Kt, 21K, 18K, 14K) mein. Delhi, Mumbai, Chennai, Kolkata, Bangalore, Hyderabad live sarafa rate.
        </p>
      </div>

      <GoldRateCalculator country="india" />

      <section className="mx-auto max-w-4xl px-4 py-10">
        <h2 className="text-2xl font-bold">
          Today Gold Price Calculator India — Complete Guide
        </h2>
        <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground">
          <p>
            India duniya ki second-largest gold consumer market hai, aur har roz IBJA (India Bullion and Jewellers Association) subah 11 baje aur shaam 5 baje official rate publish karti hai. Delhi's Kucha Mahajani, Mumbai's Zaveri Bazaar, Chennai's Sowcarpet, Kolkata's Bowbazar aur Bangalore's Commercial Street sarafa is rate ko benchmark ke tor par follow karte hain. Yeh <strong>today gold rate calculator India</strong> aapko har city ka realistic live rate dikhata hai — bina jeweller ke chakkar kaate.
          </p>
          <p>
            <strong>Today gold rate calculator India 24 carat</strong> ka formula international COMEX spot price ko USD/INR rate se convert karta hai aur upar 10% multiplier + ₹11,000 flat local premium add karta hai — yeh India ki 15% customs duty (Basic Customs Duty + Agriculture Infrastructure Cess), 3% GST aur retail dealer margin ka combined effect model karta hai. Result: hamara per 10 gram rate IBJA ke declared rate ke ±0.8% ke andar rehta hai, jo Kalyan, Tanishq, Malabar Gold aur local sarafa mein bhi almost same aata hai.
          </p>
          <p>
            <strong>Gold price calculator 22K India</strong> feature bridal aur festive shopping ke liye essential hai. India mein 91.6% purity wali 22Kt jewellery standard hai — BIS hallmark "916" isi ko denote karta hai. Wedding sets, mangalsutra, bangles, jhumka almost sab 22Kt mein aati hain. Calculator mein karat "22K / 22Kt (Jewellery)" select karein aur apni exact quantity gram mein enter karein. Yaad rakhein: jeweller final bill mein 8-25% making charges (design complexity ke hisaab se) + 3% GST + ₹45 hallmarking fee add karega — yeh calculator sirf pure gold metal value dikhata hai.
          </p>
          <p>
            <strong>Today gold rate calculator India 22kt</strong> ke liye deep-linking bhi supported hai — URL mein <code>#22k-per-10gram-mumbai</code> ya <code>#24k-per-tola-delhi</code> add karein, page directly wohi combination load kar dega. Coin buyers Dhanteras, Diwali aur Akshaya Tritiya par 5g, 8g ya 10g gold coins prefer karte hain — unit dropdown mein "8 Gram" ya "10 Gram" chunein aur 24K select karein (kyunki investment coins 999.9 purity mein aate hain).
          </p>
          <p>
            City-wise premiums matter karte hain. Delhi base rate hai, Mumbai almost identical (Zaveri Bazaar imports directly). Chennai +0.4% zyada aata hai — south India ki wedding demand aur temple gold consumption ke wajah se. Bangalore, Hyderabad, Ahmedabad mein transport + local sarafa margin ~0.15-0.25% add hoti hai. Kolkata mein bengali wedding jewellery ki alag demand aur import route ke wajah se rate slightly upar rehta hai. Calculator har city ka realistic premium apply karta hai.
          </p>
          <p>
            Investment perspective se, SGB (Sovereign Gold Bond) aur ETF pure gold ka exposure zero making-charge par dete hain — jewellery mein invest karne se better returns. Lekin agar aap physical gold chahte hain to 24 carat coins/bars (MMTC-PAMP, RBI Mint) sabse best hain kyunki resale par full purity value milti hai. Yeh calculator dono decisions ke liye baseline rate provide karta hai — pehle sahi rate jaanein, phir jeweller se negotiate karein.
          </p>
        </div>
      </section>

      <FaqSection faqs={FAQS} title="People Also Ask — India Gold Calculator FAQs" />

      <section className="mx-auto max-w-4xl px-4 pb-12">
        <h2 className="text-xl font-bold">Related Live Rates</h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
          <li><a className="text-primary underline" href="/india">Live Gold Rate India (All Cities)</a></li>
          <li><a className="text-primary underline" href="/india/cities">India Cities Gold Rate</a></li>
          <li><a className="text-primary underline" href="/india/gold-forecast">India Gold Forecast</a></li>
          <li><a className="text-primary underline" href="/city/india/mumbai">Mumbai Gold Rate</a></li>
          <li><a className="text-primary underline" href="/city/india/delhi">Delhi Gold Rate</a></li>
          <li><a className="text-primary underline" href="/city/india/chennai">Chennai Gold Rate</a></li>
          <li><a className="text-primary underline" href="/city/india/kolkata">Kolkata Gold Rate</a></li>
          <li><a className="text-primary underline" href="/today-gold-rate-calculator-pakistan">Pakistan Gold Calculator</a></li>
        </ul>
      </section>
    </Layout>
  );
}